import { useState, useEffect, useRef, useCallback } from 'react';
import Editable from '../Editable';
import { useSection } from '../useSection';
import { authFetch } from '../../auth';
import { EducationFormPreview } from '../FormPreview';

const API = '/api/cms/courses';

/** 교육 신청 — 대표 과정(첫 번째)을 DB에서 불러와 편집·저장. 정적 문구는 education/ui 섹션. */
export default function Education({ onRegisterSave, onRegisterDirty, refreshKey = 0 }) {
  const [course, setCourse] = useState(undefined); // undefined=로딩
  const ref = useRef(null);
  const dirtyRef = useRef(false);
  const { data: ui, setField, saveSection, isDirty: uiIsDirty } = useSection('education', 'ui', 1);

  useEffect(() => {
    let alive = true;
    authFetch(`${API}?lang=ko`).then((r) => r.json())
      .then((d) => { if (alive) { const c = d[0] || null; ref.current = c; setCourse(c); } })
      .catch(() => { if (alive) setCourse(null); });
    return () => { alive = false; };
  }, [refreshKey]);

  const update = (f, v) => { dirtyRef.current = true; ref.current = { ...ref.current, [f]: v }; setCourse(ref.current); };

  const save = useCallback(async () => {
    const c = ref.current;
    if (!c) return;
    const res = await authFetch(API, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ intNum: c.intNum, name: c.name, fee: c.fee, duration: c.duration, schedule: c.schedule, sort: c.sort }),
    });
    if (!res.ok) throw new Error(`교육 과정 저장 실패 (${res.status})`);
    await saveSection();
    dirtyRef.current = false;
  }, [saveSection]);

  useEffect(() => { if (onRegisterSave) onRegisterSave(save); return () => { if (onRegisterSave) onRegisterSave(null); }; }, [save, onRegisterSave]);

  const isDirty = useCallback(() => dirtyRef.current || uiIsDirty(), [uiIsDirty]);
  useEffect(() => { if (onRegisterDirty) onRegisterDirty(isDirty); return () => { if (onRegisterDirty) onRegisterDirty(null); }; }, [isDirty, onRegisterDirty]);

  if (course === undefined) {
    return <section className="section pt-page"><div className="container"><p className="heading-sub color-muted">불러오는 중…</p></div></section>;
  }

  return (
    <>
      <section className="section pt-page" style={{ paddingBottom: 'calc(var(--spacing-6xl) / 2)' }}>
        <div className="container">
          <Editable as="h1" className="heading-page" value={ui.title || '교육 신청'} onChange={(t) => setField('title', t)} />
          <Editable as="p" className="heading-sub color-muted" value={ui.intro || 'Real-Time Embedded System 개발자를 위한 교육으로, 강의와 실습을 병행하여 진행됩니다.'} onChange={(t) => setField('intro', t)} />
          {course && (
            <div className="plan-card is-static" style={{ borderRadius: 'var(--radius-md)' }}>
              <div className="plan-body">
                <Editable as="h3" value={course.name} onChange={(t) => update('name', t)} />
                <div className="price">
                  <Editable value={course.fee} onChange={(t) => update('fee', t)} />{' '}
                  <Editable as="span" className="color-muted" style={{ fontSize: 'var(--font-size-md)' }} value={ui.vatNote || '(부가세 미포함)'} onChange={(t) => setField('vatNote', t)} />
                </div>
                <div className="period"><Editable as="span" value={ui.durationLabel || '교육 기간'} onChange={(t) => setField('durationLabel', t)} /> · <Editable value={course.duration} onChange={(t) => update('duration', t)} /></div>
                <EducationFormPreview />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="section" style={{ paddingTop: 'calc(var(--spacing-6xl) / 2)' }}>
        <div className="container">
          <div className="contact-offices">
            <div className="contact-office">
              <Editable as="h3" value={ui.inquiryTitle || '교육 문의'} onChange={(t) => setField('inquiryTitle', t)} />
              <ul className="contact-info-list">
                <li><Editable as="span" value={ui.emailLabel || '이메일 문의'} onChange={(t) => setField('emailLabel', t)} />: service@acetec-korea.co.kr</li>
                <li><Editable as="span" value={ui.applyLabel || '교육 신청 문의'} onChange={(t) => setField('applyLabel', t)} />: <Editable as="span" className="link-underline" value={ui.applyPhone || '02-420-2343 (내선 721)'} onChange={(t) => setField('applyPhone', t)} /></li>
                <li><Editable value={ui.instructor || '강사: 이은옥 (컴퓨터시스템응용기술사)'} onChange={(t) => setField('instructor', t)} /></li>
                <li><Editable value={ui.venue || '교육 장소: 서울특별시 구로구 디지털로26길 61, 에이스하이엔드타워 2차 603호'} onChange={(t) => setField('venue', t)} /></li>
                <li><Editable value={ui.telfax || 'TEL: 02-420-2343 (내선 721) · FAX: 02-420-2757'} onChange={(t) => setField('telfax', t)} /></li>
              </ul>
            </div>
          </div>
          <Editable as="p" className="heading-sub-sm color-muted mt-xl" value={ui.notice || '※ 교육 2~3일 전에 개설 여부를 개별 통보해 드립니다. 행정 업무 관리상, 교육 개설 확정 통보 이후에는 취소가 불가하오니 신청 시 신중을 기해 주시기 바랍니다.'} onChange={(t) => setField('notice', t)} />
        </div>
      </section>
    </>
  );
}
